/**
 * Typed bindings for /api/audience/import.
 *
 * Rows that already carry a numeric Telegram id land in the audience straight
 * away. Rows known only by @username or phone need a real Telegram lookup, so
 * the backend queues them and this module polls `/import/status` while that
 * runs — minutes for a short list, hours for a long one (the per-account
 * lookup budget is 150/day).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export interface ImportResult {
  batch_id: string;
  /** How we read the input: json | csv | tsv | lines. */
  detected_format: string;
  parsed_rows: number;
  created: number;
  updated: number;
  skipped_noise: number;
  duplicates_in_file: number;
  pending_usernames: number;
  pending_phones: number;
  already_known: number;
  queued: boolean;
}

export interface ImportProgress {
  batch_id: string | null;
  label: string | null;
  status: "idle" | "queued" | "running" | "done" | "failed";
  total: number;
  processed: number;
  resolved: number;
  not_found: number;
  started_at: string | null;
  updated_at: string | null;
  error: string | null;
}

export const AUDIENCE_IMPORT_STATUS_KEY = ["audience_import_status"] as const;

export function useImportAudience() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      text?: string;
      file?: File;
      label?: string | null;
      resolve_missing: boolean;
    }) => {
      if (payload.file) {
        const form = new FormData();
        form.append("file", payload.file);
        if (payload.label) form.append("label", payload.label);
        form.append("resolve_missing", String(payload.resolve_missing));
        return api.upload<ImportResult>("/api/audience/import/file", form);
      }
      return api.post<ImportResult>("/api/audience/import", {
        text: payload.text ?? "",
        label: payload.label || null,
        resolve_missing: payload.resolve_missing,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === "scraped_users" });
      qc.invalidateQueries({ queryKey: AUDIENCE_IMPORT_STATUS_KEY });
      // The upload is a new source: without this it was missing from the
      // source picker until the page was left and reopened.
      qc.invalidateQueries({ queryKey: ["audience_sources"] });
    },
  });
}

/**
 * Poll while lookups are in flight. Each resolved person becomes an audience
 * row, so the list underneath is refetched as the count moves.
 */
export function useAudienceImportStatus() {
  const qc = useQueryClient();
  return useQuery<ImportProgress>({
    queryKey: AUDIENCE_IMPORT_STATUS_KEY,
    queryFn: async () => {
      const progress = await api.get<ImportProgress>("/api/audience/import/status");
      if (progress.status === "running" || progress.status === "done") {
        qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === "scraped_users" });
      }
      return progress;
    },
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "running" || status === "queued" ? 10_000 : false;
    },
  });
}
